import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDecodedParam } from '@app/hooks/useDecodedParam';
import * as Yup from 'yup';
import Select from "react-select";
import {Form} from 'react-bootstrap';
import { useFormik } from 'formik';
import { Button } from '@app/components';
import { toast } from 'react-toastify';
import { getPostcodesByFranchiseId } from '@app/services/postcode';
import { getAllActiveFranchise } from '@app/services/franchise';
import { getCentreById, updateCentre } from '@app/services/centre';

const EditCentre = () => {

    let obj: any = {}
    const location = useLocation();
    const id = useDecodedParam('encodedId', '/centers') ?? (location.state as { id?: number })?.id;
    const idRef = useRef(id); // persist id across re-renders
    const [franchises, setFranchises] = useState([]);
    const [postcodes, setPostcodes] = useState([]);
    const [selectedFranchise, setSelectedFranchise] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [isLoadingFranchise, setLoadingFranchise] = useState(false);
    const [centresData, setCentresData] = useState(obj);
    const navigate = useNavigate();
    const centerStatus = [{value:'active', label:'Active', disabled: false}, {value:'inactive', label:'Inactive', disabled: false}, {value:'archived', label:'Archived', disabled: true}]

    useEffect(() => {
        getAllActiveFranchiseData();
        getCentreDetailsById(id);
    },[]);

    const getCentreDetailsById = async (id) => {
        try {
            setLoading(true);
            const resp = await getCentreById(id);
            if(resp.success === true){
                setLoading(false);
                setCentresData(resp?.collections);
                if(resp?.collections?.franchise_id){
                    getPostcodesByFranchiseData(resp?.collections?.franchise_id)
                    setSelectedFranchise(resp?.collections?.franchise_id)
                }
            }else{
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const getAllActiveFranchiseData = async () => {
        try {
            setLoadingFranchise(true);
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) {
                setLoadingFranchise(false);
                setFranchises(resp?.collections);
            } else {
                setLoadingFranchise(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoadingFranchise(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const getPostcodesByFranchiseData = async (franchiseId) => {
        try {
                if(franchiseId){
                    const resp = await getPostcodesByFranchiseId(franchiseId);
                    if (resp?.success === true) {
                        setPostcodes(resp?.collections);
                    } else {
                        toast.error(resp?.errors[0]?.msg);
                    }
                }
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const putCentre = async (payload) => {
        const centreId = idRef.current;
        if (!centreId) {
            toast.error('Centre ID not found. Please go back and try again.');
            return;
        }
        try {
            setLoading(true);
            const resp = await updateCentre(centreId, payload);
            if(resp.success === true){
                setLoading(false);
                toast.success(resp?.message);
                navigate('/centers');
            }else{
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const { handleChange, values, handleSubmit, touched, errors, setFieldValue } = useFormik({
        initialValues: {
            franchise_id: selectedFranchise,
            postcode_id: centresData?.postcode_id || '',
            name: centresData?.name || '',
            area_code: centresData?.area_code || '',
            email: centresData?.email || '',
            phone_number: centresData?.phone_number || '',
            address: centresData?.address || '',
            status: centresData?.status || '',
        },
        validationSchema: Yup.object({
            franchise_id: Yup.string().required('Please select franchise'),
            postcode_id: Yup.string().required('Please select postcode'),
            name: Yup.string().required('Please enter centre name'),
            area_code: Yup.string().required('Please enter centre postcode'),
            email: Yup.string().required('Please enter centre email')
            .email("Enter a valid email"),
            phone_number: Yup.string().matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
            "Please enter valid number"
            ),
            // address: Yup.string().required('Please enter address'),
            status: Yup.string().required('Please select status'),
        }),
        onSubmit: (values) => {
            putCentre(values);
        },
        enableReinitialize: true,
    });

    return (
    <div>
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card card-primary">
                            <div className="card-header">
                                <h3 className="card-title">Update Centre</h3>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="franchise_id">Franchise <span className="required">*</span></label>
                                                <Select
                                                    id="franchise_id"
                                                    name="franchise_id"
                                                    placeholder="Select Franchise"
                                                    options={franchises}
                                                    getOptionValue={(option)=>`${option['id']}`}
                                                    getOptionLabel={(option)=>`${option['name']}`}
                                                    onChange={ (option) => { 
                                                        if(option){
                                                            setFieldValue("franchise_id", option.id)
                                                            getPostcodesByFranchiseData(option.id)
                                                            setFieldValue("postcode_id", '')
                                                        }else{
                                                            setFieldValue("franchise_id", '')
                                                            setPostcodes([]);
                                                            setFieldValue("postcode_id", '')
                                                        }
                                                    }}
                                                    value={franchises.filter(function(option) {
                                                        return option.id === values.franchise_id;
                                                    })}
                                                    isLoading={isLoadingFranchise}
                                                    isClearable={true}
                                                />
                                                {(touched.franchise_id && errors.franchise_id) && (
                                                    <div className="text-danger">
                                                        {errors.franchise_id}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="postcode_id">Postcode <span className="required">*</span></label>
                                                <select
                                                    className="form-control"
                                                    id="postcode_id"
                                                    name="postcode_id"
                                                    onChange={handleChange} 
                                                    value={values.postcode_id}
                                                >
                                                    <option value="">--Please select--</option>
                                                    {postcodes.map((obj) => (
                                                        <option key={obj.id} value={obj.id}>
                                                            {obj.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {(touched.postcode_id && errors.postcode_id) && (
                                                    <div className="text-danger">
                                                        {errors.postcode_id}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Centre Name<span className="required">*</span></label>
                                                <Form.Control 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="exampleInputEmail1" 
                                                    placeholder="Enter centre name"
                                                    name="name" 
                                                    onChange={handleChange} 
                                                    value={values.name}
                                                />
                                                {(touched.name && errors.name) && (
                                                    <div className="text-danger">
                                                        {errors.name}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="area_code">Area Code<span className="required">*</span></label>
                                                <Form.Control 
                                                    type="text" 
                                                    className="form-control" 
                                                    id="area_code" 
                                                    placeholder="Enter centre postcode"
                                                    name="area_code" 
                                                    onChange={handleChange} 
                                                    value={values.area_code}
                                                />
                                                {(touched.area_code && errors.area_code) && (
                                                    <div className="text-danger">
                                                        {errors.area_code}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1"> Email <span className="required">*</span></label>
                                                <Form.Control 
                                                    type="email" 
                                                    className="form-control" 
                                                    id="exampleInputEmail1" 
                                                    placeholder="Enter centre email"
                                                    name="email" 
                                                    onChange={handleChange} 
                                                    value={values.email}
                                                />
                                                {(touched.email && errors.email) && (
                                                    <div className="text-danger">
                                                        {errors.email}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Phone Number</label>
                                                <input type="text" className="form-control" id="exampleInputEmail1" placeholder="Enter phone number" 
                                                    name="phone_number" 
                                                    onChange={handleChange} 
                                                    value={values.phone_number}
                                                />
                                                {(touched.phone_number && errors.phone_number) && (
                                                    <div className="text-danger">
                                                        {errors.phone_number}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Address </label>
                                                <textarea 
                                                    className="form-control" 
                                                    id="exampleInputEmail1" 
                                                    placeholder="Enter address" 
                                                    name="address" 
                                                    onChange={handleChange} 
                                                    value={values.address}
                                                ></textarea>
                                                {(touched.address && errors.address) && (
                                                    <div className="text-danger">
                                                        {errors.address}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label htmlFor="exampleInputEmail1">Status <span className="required">*</span></label>
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    onChange={handleChange} 
                                                    value={values.status}
                                                >
                                                    <option value="">--Please select--</option>
                                                    {centerStatus.map((obj) => (
                                                        <option key={obj.value} value={obj.value} disabled={obj.disabled}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {(touched.status && errors.status) && (
                                                    <div className="text-danger">
                                                        {errors.status}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-footer">
                                    <div className='col-md-12'>
                                        <Button type="submit" isLoading={isLoading} className="btn btn-primary mr-2">Update </Button>
                                        <Link className="btn btn-default" to='/centers'>Cancel</Link>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
  )
}

export default EditCentre;